import { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TabEnum, TabType } from "../../Enums/EventEnums";
import { useEventForm } from "../../hooks/Events/useEventForm";
import { ITab } from "../../interfaces/models/IEvent";

const getTabId = (type: TabType, container: "unselected" | "selected") =>
  `${type}-${container}`;

interface DraggableTabItemProps {
  tab: TabType;
  container: "unselected" | "selected";
}

const DraggableTabItem = ({ tab, container }: DraggableTabItemProps) => {
  const isSelected = container === "selected";
  const id = getTabId(tab, container);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: {
      type: tab,
      container,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        px-6 py-3 h-9 cursor-move select-none min-w-[11.5rem] w-auto
        text-base font-medium transition-colors duration-200 text-center
        flex items-center justify-center font-decimal whitespace-nowrap
        ${
          isSelected
            ? "bg-accent-Default-dark text-white"
            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
        }
      `}
    >
      {TabEnum[tab]?.toUpperCase()}
    </div>
  );
};

interface DroppableContainerProps {
  id: "unselected" | "selected";
  title: string;
  tabs: TabType[];
}

const DroppableContainer = ({ id, title, tabs }: DroppableContainerProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { container: id },
  });

  const items = tabs.map((tab) => getTabId(tab, id));
  const isEmpty = tabs.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm text-gray-600 font-Roboto leading-5">{title}</h3>
      <div
        ref={setNodeRef}
        className={`bg-white border-2 border-dashed rounded-lg transition-colors ${
          isOver ? "border-primary bg-blue-50" : "border-gray-300"
        } ${isEmpty ? "min-h-[2rem]" : ""}`}
      >
        <SortableContext items={items} strategy={horizontalListSortingStrategy}>
          <div className={`flex flex-row flex-wrap gap-2 p-4 min-h-[3rem]`}>
            {isEmpty ? (
              <div className="text-gray-400 text-center w-full py-6 flex items-center justify-center font-medium pointer-events-none">
                Drag and drop tabs here
              </div>
            ) : (
              tabs.map((tab) => (
                <DraggableTabItem
                  key={getTabId(tab, id)}
                  tab={tab}
                  container={id}
                />
              ))
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};

const EventTabs = () => {
  const { event, updateEventField } = useEventForm();
  const allTabTypes = Object.keys(TabEnum) as TabType[];
  const { pathname } = useLocation();
  const { id: urlEventId } = useParams();
  const isEditMode = pathname.includes("edit");

  const [unselectedTabs, setUnselectedTabs] = useState<TabType[]>([]);
  const [selectedTabs, setSelectedTabs] = useState<TabType[]>([]);
  const [activeTab, setActiveTab] = useState<TabType | null>(null);
  const [activeContainer, setActiveContainer] = useState<
    "unselected" | "selected" | null
  >(null);
  const [comingSoonMap, setComingSoonMap] = useState<Map<TabType, boolean>>(
    new Map(),
  );
  const previousTabsRef = useRef<string>("");
  const previousEventIdRef = useRef<string>("");
  const isInitializingRef = useRef<boolean>(false);

  const getTabType = (tab: TabType | ITab): TabType => {
    return typeof tab === "string" ? tab : tab.type;
  };

  const isTabObject = (tab: TabType | ITab): tab is ITab => {
    return (
      typeof tab === "object" && tab !== null && "type" in tab && "index" in tab
    );
  };

  useEffect(() => {
    if (isEditMode) {
      if (
        urlEventId &&
        (!event?.id || event.id === "" || event.id !== urlEventId)
      ) {
        if (!event?.id || event.id === "") {
          setSelectedTabs([]);
          setUnselectedTabs([]);
        }
        return;
      }

      if (event?.tabs === undefined) {
        return;
      }
    }

    const eventIdChanged = previousEventIdRef.current !== event?.id;
    if (eventIdChanged) {
      previousTabsRef.current = "";
      previousEventIdRef.current = event?.id || "";
      isInitializingRef.current = true;
    }

    const existing = Array.isArray(event?.tabs) ? event.tabs : [];

    if (
      isEditMode &&
      previousTabsRef.current === "" &&
      event?.id &&
      existing.length === 0 &&
      (!event?.title || event.title === "")
    ) {
      return;
    }

    const tabsKey =
      existing.length > 0
        ? JSON.stringify(
            existing
              .map((tab) => {
                const tabType = getTabType(tab);
                const index = isTabObject(tab)
                  ? tab.index
                  : existing.indexOf(tab);
                return { type: tabType, index };
              })
              .sort((a, b) => a.index - b.index),
          )
        : "[]";

    if (previousTabsRef.current === tabsKey && !eventIdChanged) {
      isInitializingRef.current = false;
      return;
    }

    previousTabsRef.current = tabsKey;

    const newComingSoonMap = new Map<TabType, boolean>();
    const selectedTypesSet = new Set<TabType>();

    existing.forEach((tab) => {
      const tabType = getTabType(tab);
      selectedTypesSet.add(tabType);

      if (isTabObject(tab) && tab.comingSoon !== undefined) {
        newComingSoonMap.set(tabType, tab.comingSoon);
      }
    });

    const initialUnselected: TabType[] = allTabTypes.filter(
      (t) => !selectedTypesSet.has(t),
    );

    const sortedSelected = existing
      .map((tab) => {
        const tabType = getTabType(tab);
        const index = isTabObject(tab) ? tab.index : existing.indexOf(tab);
        return { type: tabType, index };
      })
      .sort((a, b) => a.index - b.index)
      .map((item) => item.type);

    isInitializingRef.current = true;
    setComingSoonMap(newComingSoonMap);
    setSelectedTabs(sortedSelected);
    setUnselectedTabs(initialUnselected);

    setTimeout(() => {
      isInitializingRef.current = false;
    }, 200);
  }, [event?.tabs, event?.id, allTabTypes, isEditMode, urlEventId]);

  const lastValidOverRef = useRef<{
    container: "unselected" | "selected";
    type: TabType | null;
  } | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current;

    if (data) {
      const container = data.container as "unselected" | "selected";
      const list = container === "selected" ? selectedTabs : unselectedTabs;
      const tab = list.find((t) => t === data.type);

      if (tab) {
        setActiveTab(tab);
        setActiveContainer(container);
        lastValidOverRef.current = null;
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over || !activeContainer) {
      return;
    }

    const activeData = active.data.current;
    const overId = over.id as string;

    if (!activeData) {
      return;
    }

    let targetContainer: "unselected" | "selected" | null = null;
    let overType: TabType | null = null;

    if (overId === "unselected" || overId === "selected") {
      targetContainer = overId;
      overType = null;

      if (targetContainer !== activeContainer) {
        lastValidOverRef.current = {
          container: targetContainer,
          type: overType,
        };
      } else if (!lastValidOverRef.current?.type) {
        lastValidOverRef.current = {
          container: targetContainer,
          type: overType,
        };
      }

      return;
    } else {
      const overData = over.data.current;
      if (overData) {
        targetContainer = overData.container;
        overType = overData.type;
        if (targetContainer) {
          lastValidOverRef.current = {
            container: targetContainer,
            type: overType,
          };
        }
      }
    }

    if (!targetContainer || !overType) {
      return;
    }

    const activeType = activeData.type as TabType;

    if (targetContainer === activeContainer) {
      const list =
        activeContainer === "selected" ? selectedTabs : unselectedTabs;
      const setList =
        activeContainer === "selected" ? setSelectedTabs : setUnselectedTabs;

      const oldIndex = list.findIndex((t) => t === activeType);
      const newIndex = list.findIndex((t) => t === overType);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newList = arrayMove(list, oldIndex, newIndex);
        setList(newList);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active } = event;

    if (!activeContainer || !lastValidOverRef.current) {
      setActiveTab(null);
      setActiveContainer(null);
      lastValidOverRef.current = null;
      return;
    }

    const activeData = active.data.current;

    if (!activeData) {
      setActiveTab(null);
      setActiveContainer(null);
      lastValidOverRef.current = null;
      return;
    }

    const targetContainer = lastValidOverRef.current.container;
    const overType = lastValidOverRef.current.type;

    const activeType = activeData.type as TabType;

    if (targetContainer !== activeContainer) {
      const sourceList =
        activeContainer === "selected" ? selectedTabs : unselectedTabs;
      const destList =
        targetContainer === "selected" ? selectedTabs : unselectedTabs;
      const setSourceList =
        activeContainer === "selected" ? setSelectedTabs : setUnselectedTabs;
      const setDestList =
        targetContainer === "selected" ? setSelectedTabs : setUnselectedTabs;

      const sourceIndex = sourceList.findIndex((t) => t === activeType);

      if (sourceIndex !== -1) {
        const newSourceList = [...sourceList];
        const [movedTab] = newSourceList.splice(sourceIndex, 1);

        const newDestList = [...destList];

        if (overType) {
          const targetIndex = newDestList.findIndex((t) => t === overType);
          if (targetIndex !== -1) {
            newDestList.splice(targetIndex, 0, movedTab);
          } else {
            newDestList.push(movedTab);
          }
        } else {
          newDestList.push(movedTab);
        }

        setSourceList(newSourceList);
        setDestList(newDestList);
      }
    }

    setActiveTab(null);
    setActiveContainer(null);
    lastValidOverRef.current = null;
  };
  useEffect(() => {
    if (previousTabsRef.current === "" || isInitializingRef.current) {
      return;
    }

    if (
      selectedTabs.length === 0 &&
      previousTabsRef.current !== "" &&
      previousTabsRef.current !== "[]"
    ) {
      return;
    }

    const tabsAsITab: ITab[] = selectedTabs.map((tabType, index) => {
      const comingSoon = comingSoonMap.get(tabType);
      const tab: ITab = {
        type: tabType,
        index: index,
      };

      if (comingSoon !== undefined) {
        tab.comingSoon = comingSoon;
      }

      return tab;
    });

    updateEventField("tabs", tabsAsITab);
  }, [selectedTabs, comingSoonMap, updateEventField]);

  const collisionDetection = (args: any) => {
    const { active } = args;
    const activeId = active?.id;

    const pointerCollisions = pointerWithin(args);

    const containerCollisions = pointerCollisions.filter(
      (c) => c.id === "unselected" || c.id === "selected",
    );

    const tabCollisions = pointerCollisions.filter(
      (c) => c.id !== "unselected" && c.id !== "selected" && c.id !== activeId,
    );

    if (tabCollisions.length > 0) {
      return [tabCollisions[0]];
    }

    if (containerCollisions.length > 0) {
      const targetContainer = containerCollisions[0].id;

      if (targetContainer !== activeContainer) {
        return [containerCollisions[0]];
      }
    }

    const centerCollisions = closestCenter(args);
    const itemCollisions = centerCollisions.filter(
      (c) => c.id !== "unselected" && c.id !== "selected" && c.id !== activeId,
    );

    if (itemCollisions.length > 0) {
      return itemCollisions;
    }

    if (containerCollisions.length > 0) {
      return containerCollisions;
    }

    return centerCollisions;
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-col gap-6 w-full">
          <DroppableContainer
            id="unselected"
            title="Unselected tabs are not visible to the member"
            tabs={unselectedTabs}
          />
          <DroppableContainer
            id="selected"
            title="Selected tabs are visible to the Member"
            tabs={selectedTabs}
          />
        </div>
        <DragOverlay>
          {activeTab ? (
            <div
              className="px-6 py-2 h-9 bg-accent-Default-dark text-white shadow-lg opacity-90 
            min-w-[11.5rem] w-auto text-center text-base font-medium flex items-center justify-center font-decimal whitespace-nowrap"
            >
              {TabEnum[activeTab]?.toUpperCase()}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default EventTabs;
